import type { Member, Role } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

export const OWNER_ROLE = 'owner';

export const isOwner = (roleKey: string): boolean => roleKey === OWNER_ROLE;

/** The signed-in member, from /me. */
export interface Actor {
  userId: string;
  roleKey: string;
}

/**
 * Roles the actor may hand out: only an owner grants owner, and nobody a role holding a permission
 * they lack (`held`, from /me). `keep` stays listed so a member's current role still shows.
 */
export function assignableRoles(
  roles: readonly Role[],
  actorRoleKey: string,
  held?: readonly string[],
  keep?: string,
): Role[] {
  const holds = (permission: string) =>
    held === undefined || held.includes('*') || held.includes(permission);
  return roles.filter(
    (role) =>
      role.key === keep ||
      (isOwner(actorRoleKey)
        ? true
        : !isOwner(role.key) && role.permissions.every((permission) => holds(permission))),
  );
}

/** What the client can already tell about changing `member`; the backend enforces the same rules. */
export interface MemberRestrictions {
  isSelf: boolean;
  /** The only owner left: cannot be demoted or removed. */
  isLastOwner: boolean;
  /** Any row action at all: never yourself, and owners only by another owner. */
  canManage: boolean;
  canRemove: boolean;
  canResend: boolean;
  roleLocked: boolean;
}

export function memberRestrictions(
  actor: Actor,
  member: Member,
  members: readonly Member[],
): MemberRestrictions {
  const isSelf = member.user.id === actor.userId;
  // Like the backend: an owner who has not accepted yet cannot keep the organisation reachable.
  const activeOwners = members.filter((m) => isOwner(m.role.key) && m.status === 'active');
  const isLastOwner =
    isOwner(member.role.key) && member.status === 'active' && activeOwners.length === 1;
  const canManage = !isSelf && (!isOwner(member.role.key) || isOwner(actor.roleKey));
  return {
    isSelf,
    isLastOwner,
    canManage,
    canRemove: canManage && !isLastOwner,
    canResend: canManage && member.status === 'invited',
    roleLocked: isLastOwner,
  };
}

export interface InviteErrorTarget {
  field: 'email' | 'roleKey' | 'storeIds' | 'root.server';
  key: string;
  values?: Record<string, unknown>;
}

/**
 * Where an invitation failure lands when the backend sent no `details` to map from: 409 on the
 * email, 422 INVALID_REFERENCE on the role or the stores (meta.storeIds), LIMIT_EXCEEDED and a
 * non-owner granting owner (403) on the form itself.
 */
export function inviteErrorTarget(error: unknown): InviteErrorTarget | null {
  if (!(error instanceof ApiError)) return null;
  switch (error.code) {
    case 'ALREADY_EXISTS':
      return { field: 'email', key: 'team:errors.emailTaken' };
    case 'INVALID_REFERENCE':
      return Array.isArray(error.meta.storeIds)
        ? { field: 'storeIds', key: 'team:errors.unknownStores' }
        : { field: 'roleKey', key: 'team:errors.unknownRole' };
    case 'LIMIT_EXCEEDED':
      return {
        field: 'root.server',
        key: 'team:errors.limit',
        values: { limit: typeof error.meta.limit === 'number' ? error.meta.limit : 200 },
      };
    default:
      return error.kind === 'forbidden'
        ? { field: 'root.server', key: 'team:errors.ownerOnly' }
        : null;
  }
}

/**
 * Changing yourself (422 CANNOT_CHANGE_SELF), demoting or removing the last owner (409
 * LAST_OWNER), a non-owner touching an owner (403). The status is the fallback for a backend
 * that still answers with the family code.
 */
export function memberChangeErrorKey(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === 'CANNOT_CHANGE_SELF') return 'team:errors.selfChange';
  if (error.code === 'LAST_OWNER') return 'team:errors.lastOwner';
  if (error.code === 'OWNER_ACCESS_FIXED') return 'team:errors.ownerAccessFixed';
  if (error.code === 'PERMISSION_DENIED' && Array.isArray(error.meta.permissions)) {
    return 'team:errors.beyondYours';
  }
  if (error.kind === 'forbidden') return 'team:errors.ownerOnly';
  if (error.kind === 'conflict') return 'team:errors.lastOwner';
  if (error.status === 422 && error.code !== 'INVALID_REFERENCE') return 'team:errors.selfChange';
  return null;
}

/** Resending only makes sense while the invitation is open; 409 PASSWORD_ALREADY_SET means it was accepted. */
export function resendErrorKey(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  return error.code === 'PASSWORD_ALREADY_SET' || error.kind === 'conflict'
    ? 'team:errors.alreadyAccepted'
    : null;
}
